import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import type { ButtonProps } from '@wartek-id/button-v1'
import { Shimmer } from '@wartek-id/shimmer'

import { isDateStarted } from 'app/KinerjaShared/utils/pelaksanaan'
import { formatDate, isValidDate } from 'utils/datetime'
import { assetUrl } from 'utils/url'
import { PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { useAPIKinerja, useAPIKinerjaProfile } from 'api/kinerja/useAPIKinerja'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { getPelaksanaanDate } from 'app/KinerjaShared/utils/pelaksanaan'
import { useUpsertPerencanaanPegawai } from 'app/KinerjaShared/hooks/useUpsertPerencanaanPegawai'
import { isValidPerencanaanStatus } from 'app/KinerjaPamongPage/utils/perencanaan'

import type { KinerjaStatusOriginal } from 'types/kinerja'

export type PerencanaanPegawaiCardProps = {
  onClick?: () => void
}

const getStateContent = (
  pelaksanaanDateLabel: string
): {
  [key in KinerjaStatusOriginal]?: {
    title: string
    description: string | React.ReactNode
    ctaText: string
    ctaColor: ButtonProps['color']
    imgPath: string
    href?: string
  }
} => ({
  NOT_FOUND: {
    title: 'Mulai Perencanaan Kinerja',
    description:
      'Isi Pelaksanaan Tugas Pokok, Praktik Kinerja, Pengembangan Kompetensi, dan Perilaku Kerja. Diskusikan juga dengan Kepala SKB (Sanggar Kegiatan Belajar).',
    ctaText: 'Mulai',
    ctaColor: 'blue',
    imgPath: '/illustrations/img-to-do-list-document.png',
  },
  DRAFT: {
    title: 'Lanjutkan Perencanaan Kinerja',
    description:
      'Isi Pelaksanaan Tugas Pokok, Praktik Kinerja, Pengembangan Kompetensi, dan Perilaku Kerja. Diskusikan juga dengan Kepala SKB (Sanggar Kegiatan Belajar).',
    ctaText: 'Lanjutkan',
    ctaColor: 'blue',
    imgPath: '/illustrations/img-to-do-list-document.png',
    href: `${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/perencanaan/ajukan`,
  },
  REVISED: {
    title: 'Menunggu pemeriksaan',
    description:
      'Perencanaan Kinerja Anda sudah diajukan. Anda dapat konfirmasi ke Kepala SKB untuk melakukan pemeriksaan.',
    ctaText: 'Cek Perencanaan Kinerja',
    ctaColor: 'white',
    imgPath: '/illustrations/img-time-document.png',
    href: `${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/perencanaan/rangkuman`,
  },
  WAITING_APPROVAL: {
    title: 'Menunggu pemeriksaan',
    description:
      'Perencanaan Kinerja Anda sudah diajukan. Anda dapat konfirmasi ke Kepala SKB untuk melakukan pemeriksaan.',
    ctaText: 'Cek Perencanaan Kinerja',
    ctaColor: 'white',
    imgPath: '/illustrations/img-time-document.png',
    href: `${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/perencanaan/rangkuman`,
  },
  APPROVED: {
    title: 'Sepakati Perencanaan dan kirim ke E-Kinerja',
    description:
      'Perencanaan Anda sudah disetujui oleh Kepala SKB. Setelah disepakati, Perencanaan akan otomatis dikirim ke E-Kinerja dalam 1x24 jam.',
    ctaText: 'Sepakati dan Kirim',
    ctaColor: 'blue',
    imgPath: '/illustrations/img-handshake.png',
    href: `${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/perencanaan/sepakati`,
  },
  READY: {
    title: 'Menunggu periode Pelaksanaan Kinerja',
    description: (
      <>
        Perencanaan Anda sudah selesai dan disepakati. Anda dapat lanjut ke
        Pelaksanaan mulai dari <strong>{pelaksanaanDateLabel}</strong>. Cek lagi
        halaman ini nanti, ya.
      </>
    ),
    ctaText: 'Cek Perencanaan Kinerja',
    ctaColor: 'blue',
    imgPath: '/illustrations/img-handshake.png',
    href: `${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/perencanaan/rangkuman`,
  },
})

export function PerencanaanPegawaiCard({
  onClick,
}: PerencanaanPegawaiCardProps) {
  const router = useRouter()

  // -----------------------------------------
  // Get Perencanaan Data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
      {
        tipe: 'SPNF',
      }
    )
  const state: KinerjaStatusOriginal | undefined = useMemo(() => {
    const status = perencanaanData?.data?.status
    if (!isValidPerencanaanStatus(status)) {
      return undefined
    }

    return status
  }, [perencanaanData])

  // -----------------------------------------
  // Resolve pelaksanaan date
  // -----------------------------------------
  const { periods, period: selectedPeriod } = usePeriodContext()

  const { pelaksanaanDateLabel, isPelaksanaanDatePassed } = useMemo(() => {
    const isResolved = Array.isArray(periods) && periods.length > 0
    const periodeKinerja = selectedPeriod.periodeKinerja
    const pelaksanaanDate = getPelaksanaanDate(periodeKinerja)
    const pelaksanaanDateLabel = isValidDate(pelaksanaanDate)
      ? formatDate(pelaksanaanDate, 'MMMM yyyy')
      : ''

    return {
      pelaksanaanDateLabel,
      isPelaksanaanDatePassed: isResolved && isDateStarted(periodeKinerja),
    }
  }, [periods, selectedPeriod])

  // -----------------------------------------
  // Get profile data
  // -----------------------------------------
  const { data: profileData } = useAPIKinerjaProfile({
    options: {
      staleTime: 1 * 24 * 60 * 60 * 1000, // 1 day
    },
  })

  // -----------------------------------------
  // Handle CTA
  // -----------------------------------------
  const { handleUpsertMutation, isLoading: isLoadingUpsertPerencanaan } =
    useUpsertPerencanaanPegawai()

  // Handle create perencanaan for NOT_FOUND state
  const handleInitPerencanaan = useCallback(() => {
    handleUpsertMutation(
      {
        tipe: 'SPNF',
        pangkatGolongan: profileData?.data?.pangkatGolongan,
        jenjang: profileData?.data?.jenjang,
      },
      {
        successMessage: 'Berhasil memulai perencanaan',
        showSuccessMessage: true,
        onSuccess: () => {
          router.push(
            `${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/perencanaan/ajukan`
          )
        },
        errorMessage: 'Gagal memulai perencanaan',
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleUpsertMutation, profileData])

  const handleCtaClick = useCallback(() => {
    // Invoke action based on state
    switch (state) {
      case 'NOT_FOUND':
        handleInitPerencanaan()
        break
      default:
        break
    }

    // Invoke onClick callback
    onClick?.()
  }, [state, handleInitPerencanaan, onClick])

  // -----------------------------------------
  // Resolve contents
  // -----------------------------------------
  const contents = getStateContent(pelaksanaanDateLabel)[state]

  if (contents === undefined) {
    return null
  }

  const { title, description, ctaText, ctaColor, imgPath, href } = contents

  // Hide card if state is READY (disetujui Kepala SKB and disepakati Pamong)
  // and pelaksanaan is started
  if (state === 'READY' && isPelaksanaanDatePassed) {
    return null
  }

  if (isPerencanaanLoading) {
    return <Shimmer className="!h-40 !w-full block" />
  }

  return (
    <section className="shadow-card rounded-lg p-6 flex items-start justify-between gap-6 bg-white">
      <div className="flex items-center gap-6">
        <img
          alt=""
          src={assetUrl(imgPath)}
          width={84}
          height={84}
          className="flex-shrink-0"
        />
        <div>
          <Text variant="heading-lg" as="h2" className="mb-1">
            {title}
          </Text>
          <Text color="subdued">{description}</Text>
        </div>
      </div>
      {href ? (
        <NextLink href={href} passHref>
          <Button
            size="lg"
            as="a"
            type={undefined}
            color={ctaColor}
            className="min-w-60 whitespace-nowrap"
            disabled={!ctaText}
            onClick={onClick}
          >
            {ctaText}
          </Button>
        </NextLink>
      ) : (
        <Button
          type="button"
          size="lg"
          color={ctaColor}
          className="min-w-60 whitespace-nowrap"
          disabled={!ctaText || isLoadingUpsertPerencanaan}
          isLoading={isLoadingUpsertPerencanaan}
          onClick={handleCtaClick}
        >
          {ctaText}
        </Button>
      )}
    </section>
  )
}
